import { Calendar, Clock, MarkerPin01, Trash01 } from "@untitledui/icons";
import { useCallback } from "react";
import { Badge } from "../base/badges/badges";
import { PayRateType } from "@fp/admin-api-client/types/payRateType.js";
import { AddressLine, formatAddress } from "../common/address";
import { formatLocalDate } from "../common/local-date-time";
import type { OpportunityTemplateDto } from "@fp/admin-api-client/types/opportunityTemplateDto.js";
import type { PayRateDto } from "@fp/admin-api-client/types/payRateDto.js";
import type { AddressDto } from "@fp/admin-api-client/types/addressDto.js";

type TemplateItem = {
	id: string;
	templateName?: string;
	title?: string | null;
	description?: string | null;
	payRate?: PayRateDto;
	address?: AddressDto;
	startDate?: string | null;
	endDate?: string | null;
};

type OpportunityTemplateListItemProps = {
	item: TemplateItem;
	onSelect: (templateId: string) => void;
	onDelete?: (templateId: string) => void;
};

export const OpportunityTemplateListItem = ({
	item,
	onSelect,
	onDelete,
}: OpportunityTemplateListItemProps) => {
	const handleClick = useCallback(() => {
		onSelect(item.id.toString());
	}, [onSelect, item.id]);

	const handleDelete = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onDelete?.(item.id.toString());
		},
		[onDelete, item.id],
	);

	const address = formatAddress(item.address);

	return (
		<button
			type="button"
			className="flex flex-col text-left border w-full border-primary rounded-lg p-4 space-y-1 bg-white"
			onClick={handleClick}
		>
			{item.templateName && (
				<>
					<div className="flex flex-row items-center justify-between">
						<h3 className="text-lg font-semibold text-primary">{item.templateName}</h3>
						<div className="flex items-center gap-2">
							{onDelete && (
								<Trash01
									className="h-4 w-4 cursor-pointer text-quaternary hover:text-error-primary"
									onClick={handleDelete}
								/>
							)}
							<Badge type="pill-color" color="indigo">
								Template
							</Badge>
						</div>
					</div>
					<div className="border-t border-gray-300 my-1" />
				</>
			)}
			<div className="flex flex-row items-center justify-between">
				<h3 className="text-4xl font-semibold text-primary">
					{item.payRate?.type !== PayRateType.Percentage && "$"}
					{item.payRate?.value}
					{item.payRate?.type === PayRateType.Hourly && "/ HR"}
					{item.payRate?.type === PayRateType.Percentage && "%"}
				</h3>
				{!item.templateName && (
					<div className="flex items-center gap-2">
						{onDelete && (
							<Trash01
								className="h-4 w-4 cursor-pointer text-quaternary hover:text-error-primary"
								onClick={handleDelete}
							/>
						)}
						<Badge type="pill-color" color="indigo">
							Template
						</Badge>
					</div>
				)}
			</div>
			<h3 className="text-lg font-semibold text-primary">{item.title}</h3>
			{item.address && address && (
				<div className="flex text-sm items-center text-quaternary">
					<MarkerPin01 className="h-4 w-4 mr-1" />
					<AddressLine address={item.address} />
				</div>
			)}
			{item.startDate && (
				<div className="flex flex-row items-center text-sm text-quaternary">
					<Clock className="h-4 w-4 mr-1" />{" "}
					{formatLocalDate(item.startDate, "time")}
				</div>
			)}
			{item.endDate && (
				<div className="flex flex-row items-center text-sm text-quaternary">
					<Calendar className="h-4 w-4 mr-1" /> by{" "}
					{formatLocalDate(item.endDate, "dateWithYear")}
				</div>
			)}
			{item.description && (
				<p className="text-sm text-quaternary line-clamp-2">
					{item.description}
				</p>
			)}
		</button>
	);
};

type OpportunityTemplateListProps = {
	items: OpportunityTemplateDto[];
	onItemSelect: (templateId: string) => void;
	onItemDelete?: (templateId: string) => void;
};

export const OpportunityTemplateList = ({
	items,
	onItemSelect,
	onItemDelete,
}: OpportunityTemplateListProps) => {
	return (
		<div className="flex flex-col gap-4">
			{items.map((item) => (
				<OpportunityTemplateListItem
					key={item.id}
					item={item}
					onSelect={onItemSelect}
					onDelete={onItemDelete}
				/>
			))}
		</div>
	);
};